import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from './AuthContext.jsx';
import { useTheme } from './hooks/useTheme.jsx';
import { useVersionedApi } from './utils/versionedApi';
import araSyncLogo from './assets/AraSync-Solid-Core-Stacked.svg';
import araSyncLogoReverse from './assets/AraSync-Solid-Core-Stacked-Reverse.svg';
import LoginBrandPanel from './components/login/LoginBrandPanel';
import { LoginErrorNotice, LoginPasswordInput, LoginSubmitButton, LoginThemeToggle, loginInputProps } from './components/login/LoginControls';

import { safeStorage } from './utils/safeStorage';
import { isPlatformAdminUser } from './utils/platformAccess';
import { restrictedHome } from './utils/accountKind';
export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [searchParams] = useSearchParams();
  const { setUser } = useAuth();
  // ThemeProvider wraps this route and honours system preference, so html.dark
  // can be set before anyone signs in. The page background is an inline
  // gradient (no dark: variant reaches it), so it must be chosen in JS.
  const { isDark } = useTheme();
  const apiClient = useVersionedApi('v1');

  // Get organization from URL parameter
  const orgParam = searchParams.get('org');
  const logoutReason = searchParams.get('reason');

  // Auto-clear corrupted/invalid legacy auth data on mount
  useEffect(() => {
    // Check if user was logged out due to inactivity
    if (logoutReason === 'inactivity') {
      setError('You were automatically logged out due to 30 minutes of inactivity. Please log in again.');
    }

    // Clean up any legacy auth data still in localStorage (transition period)
    const legacyAuth = safeStorage.getItem('evv_token');
    if (legacyAuth) {
      try {
        const payload = JSON.parse(atob(legacyAuth.split('.')[1]));
        if (!payload.organizationId || !payload.userId) {
          console.warn('Clearing invalid legacy auth data');
          safeStorage.removeItem('evv_token');
          safeStorage.removeItem('evv_user');
          safeStorage.removeItem('evv_user_role');
          safeStorage.removeItem('evv_user_permissions');
          safeStorage.removeItem('evv_user_name');
          sessionStorage.clear();
          setError('Your previous session was invalid and has been cleared. Please log in again.');
        }
      } catch (e) {
        console.warn('Clearing corrupted legacy auth data');
        safeStorage.removeItem('evv_token');
        safeStorage.removeItem('evv_user');
        safeStorage.removeItem('evv_user_role');
        safeStorage.removeItem('evv_user_permissions');
        safeStorage.removeItem('evv_user_name');
        sessionStorage.clear();
      }
    }
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await apiClient.post(`/users/login`, {
        email,
        password
      });

      const { user, token } = response.data;
      // Store token in localStorage — required because frontend and backend are on
      // different origins (app.arasynchealth.com vs evv-backend.azurecontainerapps.io),
      // so httpOnly cookies cannot be shared cross-origin.
      // TODO: When frontend and backend share a domain (e.g., api.arasynchealth.com),
      // switch to httpOnly cookie only and remove this localStorage write.
      if (token) {
        safeStorage.setItem('evv_token', token);
      }
      safeStorage.setItem('evv_user', JSON.stringify(user));
      // evv_user_cache: minimal object for AuthContext session detection only
      safeStorage.setItem('evv_user_cache', JSON.stringify({
        id: user.id,
        firstName: user.firstName,
        lastName: user.lastName,
        role: user.role,
        email: user.email,
        organizationId: user.organizationId,
        accountKind: user.accountKind || null,
      }));
      safeStorage.setItem('evv_user_role', user.role);
      safeStorage.setItem('evv_user_permissions', JSON.stringify(user.permissions || []));
      safeStorage.setItem('evv_user_name', `${user.firstName} ${user.lastName}`);
      if (user.organization) {
        safeStorage.setItem('evv_organization', JSON.stringify(user.organization));
      }
      setUser(user); // Update AuthContext immediately

      // Add a small delay to ensure localStorage writes before redirect
      setTimeout(() => {
        // Preserve organization context in navigation
        const adminUrl = orgParam ? `/admin?org=${orgParam}` : '/admin';

        // Post-login we do a REAL document navigation (window.location.assign)
        // rather than react-router navigate(). This is deliberate:
        //
        // navigate() is history.pushState — it issues NO HTTP request, so a tab
        // that has been open for hours keeps running whatever JS bundle it
        // started with. Users whose session expired (prod JWTs last 8h) would log
        // back in and still be executing yesterday's build, which is why stale-UI
        // reports ("I only see the update in incognito") kept recurring. A real
        // navigation re-fetches index.html — which nginx serves no-store — so the
        // browser picks up the current hashed assets and every session starts on
        // the deployed build.
        //
        // Safe here specifically because login is the one moment with no unsaved
        // work to lose, and the auth token + user cache were written to
        // localStorage above (before this timeout), so the reloaded document
        // rehydrates the session instead of bouncing back to /login.
        const go = (path) => window.location.assign(path);

        // One page may ask to be brought back after sign-in: the Claude connector
        // consent page (pages/ConnectClaude.jsx). Only that exact path shape is
        // honoured, so the stored value can never send anyone off-site.
        let returnTo = null;
        try {
          returnTo = window.sessionStorage.getItem('evv_return_to');
          window.sessionStorage.removeItem('evv_return_to');
        } catch (_e) {
          returnTo = null;
        }
        const connectReturn = returnTo && /^\/connect\/claude\?request=[A-Za-z0-9._-]{20,8192}$/.test(returnTo) ? returnTo : null;

        if (restrictedHome(user)) {
          // A pharmacy login has its own page and nothing else.
          go(restrictedHome(user));
        } else if (connectReturn) {
          go(connectReturn);
        } else if (user.role === 'super_admin') {
          if (isPlatformAdminUser(user)) {
            go('/platform');
          } else {
            // Organization super_admin goes to their organization's admin dashboard
            go(adminUrl);
          }
        } else if (user.role === 'admin') {
          go(adminUrl);
        } else if (user.role === 'custom') {
          go(adminUrl); // Manager goes to admin interface with limited permissions
        } else {
          // Caregivers should use the mobile app, but redirect to admin for web access
          go(adminUrl);
        }
      }, 100);
    } catch (err) {
      console.error('❌ Login error:', err);

      const status = err.response?.status;
      const code = err.response?.data?.code;
      const serverMsg = err.response?.data?.error;

      // A corrupted/stale token in the payload is the only case where clearing
      // local auth data makes sense on the login screen. A normal bad-credentials
      // 401 is NOT a session problem — the user is logging in fresh.
      if (code === 'INVALID_TOKEN_PAYLOAD' ||
          (typeof serverMsg === 'string' && serverMsg.toLowerCase().includes('authentication token'))) {
        console.log('🧹 Clearing corrupted authentication data...');
        ['evv_token','evv_user','evv_user_role','evv_user_permissions','evv_user_name']
          .forEach(k => safeStorage.removeItem(k));
        sessionStorage.clear();
        setError('Your saved sign-in data was invalid and has been cleared. Please sign in again.');
      } else if (!err.response) {
        // No HTTP response = request timed out or couldn't reach the server.
        setError(err.code === 'ECONNABORTED'
          ? 'The server took too long to respond. Check your connection and try again.'
          : 'Could not reach the server. Check your connection and try again.');
      } else if (status === 401 || code === 'INVALID_CREDENTIALS') {
        setError(serverMsg || 'Invalid email or password.');
      } else if (status === 403 || code === 'ACCOUNT_DISABLED') {
        setError(serverMsg || 'Your account is disabled. Please contact your administrator.');
      } else if (status === 429) {
        setError('Too many sign-in attempts. Please wait a moment and try again.');
      } else if (status >= 500) {
        setError(serverMsg || 'Something went wrong on our end. Please try again in a moment.');
      } else {
        setError(serverMsg || 'Sign in failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    // `min-h-screen` alone could never scroll here: index.css pins
    // `body { height: 100vh; overflow: hidden }`, so the page has no scrollbar
    // and anything past the fold is unreachable. On a short window (laptop with
    // a taskbar, below the lg breakpoint so the 12rem mobile logo also renders)
    // that put the Sign in button off-screen with no way to reach it.
    //
    // The admin app survives the global clip because AdminShell supplies its own
    // overflow-auto <main>; standalone routes like this one must bring their own.
    // Inner wrapper is min-h-full so it fills the viewport when content is short
    // and GROWS when content is tall — growing is what makes the scroll real.
    <div
      className="h-screen w-screen overflow-y-auto"
      style={{
        background: isDark
          ? 'linear-gradient(to bottom right, #111827, #0B1220, #111827)'
          : 'linear-gradient(to bottom right, #F5F7FA, #FFFFFF, #F5F7FA)',
      }}
    >
     <div className="min-h-full flex">
      {/* Left side - Branding: the logo over a soft glow, with the care-team film */}
      <LoginBrandPanel />

      {/* Right side - Login Form */}
      {/* py-10 keeps the form off the viewport edges once it is tall enough to scroll */}
      <div className="relative box-border flex w-full items-center justify-center px-8 sm:px-12 py-10 lg:w-[620px] lg:flex-none lg:px-16">
        <LoginThemeToggle />
        <div className="w-full max-w-md space-y-8">
          {/* Mobile logo */}
          <div className="lg:hidden text-center">
            <img
              src={isDark ? araSyncLogoReverse : araSyncLogo}
              alt="AraSync Health Systems — Systems That Put People First"
              className="w-56 max-w-full h-auto mx-auto mb-5"
            />
          </div>

          <div>
            <h2 className="text-center text-3xl font-bold tracking-tight text-gray-900 dark:text-white">
              Sign in to your account
            </h2>
            <p className="mt-2 text-center text-sm text-gray-600 dark:text-gray-400">
              Access your healthcare management system
            </p>
          </div>

          <form className="mt-8 space-y-6" onSubmit={handleLogin}>
            <div className="space-y-4">
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Email address
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  {...loginInputProps()}
                  placeholder="name@example.com"
                  aria-describedby="email-hint"
                />
                <p id="email-hint" className="!mx-0 !mb-0 !mt-1.5 text-xs text-gray-600 dark:text-gray-400">
                  Use the email your account was created with.
                </p>
              </div>

              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Password
                </label>
                <LoginPasswordInput value={password} onChange={(e) => setPassword(e.target.value)} />
              </div>
            </div>

            {error && <LoginErrorNotice>{error}</LoginErrorNotice>}

            <div>
              <LoginSubmitButton busy={loading} busyLabel="Signing in...">Sign in</LoginSubmitButton>
            </div>

            <div className="flex flex-col gap-2 text-center">
              <p className="!m-0 text-sm text-gray-700 dark:text-gray-300">
                Need help signing in? Contact your organization&apos;s administrator.
              </p>
              <p className="!m-0 text-xs text-gray-500 dark:text-gray-400">
                Secure login powered by AraSync Platform
              </p>
            </div>
          </form>
        </div>
      </div>
     </div>
    </div>
  );
}
